import * as React from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { Location as HistoryLocation } from 'history';
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';

interface UseRouterConfirmMessageProps {
    isDirty: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
}

interface UseRouterConfirmMessageReturn {
    render: () => JSX.Element;
}

export default function useRouterConfirmMessage({
    title = 'Änderungen verwerfen?',
    message = ' ',
    isDirty,
    onConfirm,
    onCancel,
    confirmText = 'Ja',
    cancelText = 'Nein',
}: UseRouterConfirmMessageProps): UseRouterConfirmMessageReturn {
    const history = useHistory();

    const [confirmState, setConfirmState] = React.useState({
        visible: false,
        newLocation: '',
    });

    const handleConfirmMessage = (newLocation: HistoryLocation): boolean => {
        setConfirmState({
            visible: true,
            newLocation: newLocation.pathname + newLocation.search,
        });
        return false;
    };

    const render = React.useCallback(() => {
        return (
            <>
                <Prompt when={isDirty && !confirmState.visible} message={handleConfirmMessage} />
                {confirmState.visible && (
                    <Dialog
                        hidden={false}
                        dialogContentProps={{
                            title,
                            subText: message,
                            type: DialogType.normal,
                        }}
                    >
                        <DialogFooter>
                            <DefaultButton
                                onClick={() => {
                                    setConfirmState({
                                        visible: false,
                                        newLocation: '',
                                    });
                                    if (onConfirm) {
                                        onConfirm();
                                    } else {
                                        history.push(confirmState.newLocation);
                                    }
                                }}
                                text={confirmText}
                            />
                            <PrimaryButton
                                onClick={() => {
                                    setConfirmState({
                                        visible: false,
                                        newLocation: '',
                                    });
                                    if (onCancel) {
                                        onCancel();
                                    }
                                }}
                                text={cancelText}
                            />
                        </DialogFooter>
                    </Dialog>
                )}
            </>
        );
    }, [title, message, cancelText, confirmText, confirmState, isDirty, onCancel, onConfirm, history]);

    return {
        render,
    };
}
